import { useEffect, useMemo, useState, useRef } from "react";
import { useLocation } from "react-router-dom";
import api from "@/lib/api";
import { eur, fmtTime } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/common/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Plus, Minus, Trash2, X, Lock, Unlock, Receipt, FlaskConical, CalendarCheck, FileText, Ticket, ArrowDownCircle, ArrowUpCircle, Pencil, Printer, Gift, Percent, Ban, AlertTriangle, Wallet, Search } from "lucide-react";
import { useServiceCategories, colorFor, solid, tint, UNCATEGORIZED } from "@/lib/categories";

const METHODS = ["Contanti", "Carta", "Bancomat", "Gift Card", "Credito", "Bonifico"];
const MOVEMENT_CATEGORIES = [
  { v: "rimborso", l: "Rimborso" }, { v: "acquisti", l: "Acquisti" },
  { v: "versamento", l: "Versamento" }, { v: "prelievo", l: "Prelievo" },
  { v: "spese_varie", l: "Spese varie" }, { v: "fondo_aggiuntivo", l: "Fondo aggiuntivo" },
  { v: "altro", l: "Altro" },
];

// uid stabile per le "key" React di liste editabili (append/remove) senza affidarsi all'indice
let _uidSeq = 0;
const uid = () => `row-${++_uidSeq}`;

export default function Cassa() {
  const location = useLocation();
  const [services, setServices] = useState([]);
  const [products, setProducts] = useState([]);
  const [clients, setClients] = useState([]);
  const [team, setTeam] = useState([]);
  const [cart, setCart] = useState([]);
  const [clientId, setClientId] = useState("");
  const [checkingOut, setCheckingOut] = useState(false);
  const idemKeyRef = useRef(null);
  const [operatorId, setOperatorId] = useState("");
  const [payments, setPayments] = useState([{ method: "Contanti", amount: 0, _uid: uid() }]);
  const [consumptions, setConsumptions] = useState([]);
  const [session, setSession] = useState(null);
  const [sales, setSales] = useState([]);
  const [openClose, setOpenClose] = useState(false);
  const [counted, setCounted] = useState("");
  const [appointmentId, setAppointmentId] = useState(null);
  const [checkoutClientName, setCheckoutClientName] = useState("");
  const [voucherCode, setVoucherCode] = useState("");
  const [appliedVouchers, setAppliedVouchers] = useState([]);
  const [voucherBusy, setVoucherBusy] = useState(false);
  const [referralWelcome, setReferralWelcome] = useState(null);
  const [catalog, setCatalog] = useState({ packages: [], subscriptions: [] });
  const [entitlements, setEntitlements] = useState({ packages: [] });
  const [manualUncover, setManualUncover] = useState(() => new Set());
  // --- Saldo cliente (insoluti + crediti) ---
  const [clientBalance, setClientBalance] = useState({ open_debts_total: 0, credit_available: 0, debts: [] });
  const [markInsoluto, setMarkInsoluto] = useState(false);
  const [insolutoNote, setInsolutoNote] = useState("");
  const [genCredit, setGenCredit] = useState(false);
  const [payDebt, setPayDebt] = useState(null);
  const [payDebtAmount, setPayDebtAmount] = useState("");
  const [payDebtMethod, setPayDebtMethod] = useState("Contanti");
  const [payDebtBusy, setPayDebtBusy] = useState(false);
  const loadBalance = () => {
    if (!clientId) { setClientBalance({ open_debts_total: 0, credit_available: 0, debts: [] }); return; }
    api.get(`/clients/${clientId}/balance`).then((r) => setClientBalance(r.data)).catch(() => {});
  };
  useEffect(() => { loadBalance(); setMarkInsoluto(false); setGenCredit(false); /* eslint-disable-next-line */ }, [clientId]);
  const toggleUncover = (refId) => setManualUncover((s) => { const n = new Set(s); if (n.has(refId)) n.delete(refId); else n.add(refId); return n; });

  // Badge referral: se la cliente selezionata ha un referral confermato non ancora
  // usato per lo sconto benvenuto, mostra che il -X% verrà applicato all'incasso.
  useEffect(() => {
    if (!clientId) { setReferralWelcome(null); return; }
    let alive = true;
    api.get(`/clients/${clientId}/referral`).then((r) => {
      if (!alive) return;
      const ar = r.data?.as_referred;
      if (ar && ar.status === "confirmed" && !ar.discount_applied) setReferralWelcome(r.data.new_client_discount_pct || 10);
      else setReferralWelcome(null);
    }).catch(() => { if (alive) setReferralWelcome(null); });
    return () => { alive = false; };
  }, [clientId]);

  // Entitlements della cliente (pacchetti attivi) per la scala automatica in Cassa
  useEffect(() => {
    if (!clientId) { setEntitlements({ packages: [] }); setManualUncover(new Set()); return; }
    api.get(`/clients/${clientId}/entitlements`).then((r) => setEntitlements(r.data)).catch(() => setEntitlements({ packages: [] }));
    setManualUncover(new Set());
  }, [clientId]);

  // Copertura pacchetto per riga servizio (ref_id -> {package_id, package_name})
  const coverage = useMemo(() => {
    const map = {}; const used = {};
    (cart || []).forEach((line) => {
      if (line.item_type !== "service" || manualUncover.has(line.ref_id) || map[line.ref_id]) return;
      for (const p of (entitlements.packages || [])) {
        const it = (p.items || []).find((i) => i.service_id === line.ref_id && i.available > 0);
        if (!it) continue;
        const k = `${p.id}:${line.ref_id}`;
        if ((it.available - (used[k] || 0)) >= line.quantity) {
          map[line.ref_id] = { package_id: p.id, package_name: p.name };
          used[k] = (used[k] || 0) + line.quantity;
          break;
        }
      }
    });
    return map;
  }, [cart, entitlements, manualUncover]);
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const canCancelSale = ["admin", "manager"].includes(user?.role);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelBusy, setCancelBusy] = useState(false);
  const confirmCancelSale = async () => {
    if (!cancelTarget) return;
    setCancelBusy(true);
    try {
      await api.post(`/sales/${cancelTarget.id}/cancel`, {});
      toast.success("Vendita annullata: effetti e pacchetti ripristinati");
      setCancelTarget(null);
      loadSales();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore annullamento vendita");
    } finally { setCancelBusy(false); }
  };
  const [fundOpen, setFundOpen] = useState(false);
  const [openingFloat, setOpeningFloat] = useState("");
  const [movementOpen, setMovementOpen] = useState(false);
  const [movement, setMovement] = useState({ type: "out", method: "Contanti", category: "spese_varie", amount: "", description: "" });
  const [closeNotes, setCloseNotes] = useState("");
  const [summary, setSummary] = useState(null);
  const [editSale, setEditSale] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  // --- Sconto percentuale (simulazione fino ad "Applica sconto") ---
  const [discPct, setDiscPct] = useState("");
  const [discScope, setDiscScope] = useState(() => new Set());
  const [discTouched, setDiscTouched] = useState(false);
  const [appliedPct, setAppliedPct] = useState(null); // { pct:number, keys:string[] }

  const loadSession = () => api.get("/cash/current").then((r) => setSession(r.data));
  const loadSales = () => { const s = new Date(); s.setHours(0,0,0,0); api.get(`/sales?start=${s.toISOString()}&end=${new Date().toISOString()}`).then((r) => setSales(r.data)); };

  useEffect(() => {
    api.get("/services").then((r) => setServices(r.data));
    api.get("/products").then((r) => setProducts(r.data));
    api.get("/clients").then((r) => setClients(r.data));
    api.get("/team").then((r) => setTeam(r.data));
    api.get("/packages").then((r) => setCatalog(r.data)).catch(() => {});
    loadSession(); loadSales();
  }, []);

  // Fase 5.2.4 — Checkout da appuntamento: precompila carrello dai dati passati dall'Agenda
  useEffect(() => {
    const ap = location.state?.checkoutAppointment;
    if (!ap) return;
    setAppointmentId(ap.id);
    setCheckoutClientName(ap.client_name || "");
    if (ap.client_id) setClientId(ap.client_id);
    if (ap.operator_id) setOperatorId(ap.operator_id);
    setCart((ap.lines || []).map((l) => ({
      ref_id: l.service_id, item_type: "service", name: l.name || "Servizio",
      unit_price: Number(l.unit_price || 0), quantity: Number(l.quantity || 1),
      discount: 0, category: l.category, operator_id: ap.operator_id || "",
    })));
    // Bonus: precompila il consumo materiali dalla Formula Colore registrata (ingredienti reali)
    api.get(`/appointments/${ap.id}/formula`).then((r) => {
      const fs = r.data.formulas || [];
      const cons = [];
      fs.forEach((f) => (f.ingredients || []).forEach((ing) => {
        if (ing.product_id && Number(ing.qty) > 0) {
          const ex = cons.find((x) => x.product_id === ing.product_id);
          if (ex) ex.qty = Number(ex.qty) + Number(ing.qty);
          else cons.push({ product_id: ing.product_id, qty: Number(ing.qty), _uid: uid() });
        }
      }));
      if (cons.length) setConsumptions(cons);
    }).catch(() => {});
    // pulisce lo stato di navigazione per evitare ri-precompilazioni al refresh
    window.history.replaceState({}, document.title);
  }, [location.state]);

  const lineNet = (i) => (i.item_type === "service" && coverage[i.ref_id]) ? 0 : (i.unit_price * i.quantity - (i.discount || 0));
  const total = useMemo(() => cart.reduce((s, i) => s + lineNet(i), 0), [cart, coverage]);
  const paid = useMemo(() => payments.reduce((s, p) => s + Number(p.amount || 0), 0), [payments]);
  const voucherDiscount = useMemo(() => appliedVouchers.reduce((s, v) => s + (v.applied || 0), 0), [appliedVouchers]);
  const netTotal = useMemo(() => Math.max(0, total - voucherDiscount), [total, voucherDiscount]);

  // --- Sconto percentuale: simulazione live + importo applicato ---
  const svcKey = (x) => `${x.item_type}-${x.ref_id}`;
  const serviceLinesDisc = useMemo(() => cart.filter((x) => x.item_type === "service" && lineNet(x) > 0), [cart, coverage]);
  const allDiscKeys = useMemo(() => serviceLinesDisc.map(svcKey), [serviceLinesDisc]);
  const effectiveDiscKeys = useMemo(() => discTouched ? allDiscKeys.filter((k) => discScope.has(k)) : allDiscKeys, [discTouched, discScope, allDiscKeys]);
  const allDiscSelected = serviceLinesDisc.length > 0 && effectiveDiscKeys.length === serviceLinesDisc.length;
  const pctNum = Math.min(100, Math.max(0, Number(discPct) || 0));
  const lineGross = (l) => l.unit_price * l.quantity - (l.discount || 0);
  const simDiscountAmount = useMemo(() => serviceLinesDisc.filter((l) => effectiveDiscKeys.includes(svcKey(l))).reduce((s, l) => s + Number((lineGross(l) * pctNum / 100).toFixed(2)), 0), [serviceLinesDisc, effectiveDiscKeys, pctNum]);
  const appliedAmount = useMemo(() => {
    if (!appliedPct) return 0;
    return serviceLinesDisc.filter((l) => appliedPct.keys.includes(svcKey(l))).reduce((s, l) => s + Number((lineGross(l) * appliedPct.pct / 100).toFixed(2)), 0);
  }, [appliedPct, serviceLinesDisc]);
  const finalTotal = useMemo(() => Math.max(0, netTotal - appliedAmount), [netTotal, appliedAmount]);

  const paidV = paid;
  const remaining = useMemo(() => Number((finalTotal - paidV).toFixed(2)), [finalTotal, paidV]);

  const addItem = (item, type) => {
    setCart((c) => {
      const ex = c.find((x) => x.ref_id === item.id && x.item_type === type);
      if (ex) return c.map((x) => x === ex ? { ...x, quantity: x.quantity + 1 } : x);
      return [...c, { ref_id: item.id, item_type: type, name: item.name, unit_price: item.price, quantity: 1, discount: 0, category: item.category, operator_id: operatorId }];
    });
    // Fase 5B: precompila il consumo materiali dalla ricetta standard del servizio
    if (type === "service" && Array.isArray(item.standard_recipe) && item.standard_recipe.length) {
      setConsumptions((cs) => {
        const next = [...cs];
        item.standard_recipe.forEach((r) => {
          const ex = next.find((x) => x.product_id === r.product_id);
          if (ex) ex.qty = Number(ex.qty) + Number(r.qty);
          else next.push({ product_id: r.product_id, qty: Number(r.qty), _uid: uid() });
        });
        return next;
      });
    }
  };
  const setCons = (i, k, v) => setConsumptions((cs) => cs.map((x, idx) => idx === i ? { ...x, [k]: v } : x));
  const addCons = () => setConsumptions((cs) => [...cs, { product_id: "", qty: 0, _uid: uid() }]);
  const rmCons = (i) => setConsumptions((cs) => cs.filter((_, idx) => idx !== i));
  const consumableProducts = products.filter((p) => p.type !== "retail");
  const updQty = (i, n) => setCart((c) => c.map((x, idx) => idx === i ? { ...x, quantity: Math.max(1, x.quantity + n) } : x));
  const updDisc = (i, v) => setCart((c) => c.map((x, idx) => idx === i ? { ...x, discount: Number(v || 0) } : x));
  const rm = (i) => setCart((c) => c.filter((_, idx) => idx !== i));

  // --- Azioni sconto percentuale (solo simulazione finché non si preme "Applica sconto") ---
  const toggleDiscKey = (k) => {
    setDiscScope((s) => { const base = discTouched ? new Set(s) : new Set(allDiscKeys); if (base.has(k)) base.delete(k); else base.add(k); return base; });
    setDiscTouched(true);
  };
  const toggleAllDisc = () => { setDiscScope(() => allDiscSelected ? new Set() : new Set(allDiscKeys)); setDiscTouched(true); };
  const applyDiscount = () => {
    if (pctNum <= 0) { toast.error("Inserisci una percentuale valida"); return; }
    if (effectiveDiscKeys.length === 0) { toast.error("Seleziona almeno un servizio"); return; }
    setAppliedPct({ pct: pctNum, keys: effectiveDiscKeys.slice() });
    toast.success(`Sconto ${pctNum}% applicato al totale`);
  };
  const removeDiscount = () => { setAppliedPct(null); toast.message("Sconto rimosso — totale ripristinato"); };
  const useReferralPromo = () => { setDiscScope(new Set()); setDiscTouched(false); setDiscPct(String(referralWelcome || 10)); toast.message("Promozione caricata nella simulazione"); };

  // --- Voucher premio (Identity Rewards): solo anteprima, lo sconto è applicato dal backend ---
  const applyVoucher = async () => {
    const code = voucherCode.trim().toUpperCase();
    if (!code) return;
    if (appliedVouchers.some((v) => v.code === code)) { toast.error("Voucher già applicato"); return; }
    setVoucherBusy(true);
    try {
      const { data } = await api.get(`/rewards/vouchers/lookup?code=${encodeURIComponent(code)}`);
      if (!data.valid) { toast.error(`Voucher non valido: ${data.reason || "non utilizzabile"}`); return; }
      const v = data.voucher;
      if (appliedVouchers.length > 0 && (v.stackable === false || appliedVouchers.some((x) => x.stackable === false))) {
        toast.error("Questo voucher non è cumulabile con altri"); return;
      }
      const key = `${v.item_type}-${v.item_ref_id}`;
      const line = cart.find((x) => `${x.item_type}-${x.ref_id}` === key);
      if (!line) { toast.error(`Aggiungi prima "${v.item_name}" al carrello per applicare il premio`); return; }
      const alreadyOnLine = appliedVouchers.filter((x) => x.item_key === key).reduce((s, x) => s + x.applied, 0);
      const remaining = Math.max(0, line.unit_price * line.quantity - (line.discount || 0) - alreadyOnLine);
      const applied = Math.min(remaining, Number(v.value_eur) || 0);
      if (applied <= 0) { toast.error("Nessuno sconto applicabile (articolo già coperto)"); return; }
      setAppliedVouchers((a) => [...a, { code: v.code, reward_name: v.reward_name, value_eur: v.value_eur, item_key: key, item_name: v.item_name, applied, stackable: v.stackable !== false }]);
      setVoucherCode("");
      toast.success(`Premio applicato: -${eur(applied)} su ${v.item_name}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Voucher non trovato"); }
    finally { setVoucherBusy(false); }
  };
  const removeVoucher = (code) => setAppliedVouchers((a) => a.filter((x) => x.code !== code));

  const checkout = async () => {
    if (checkingOut) return; // guard anti doppio invio lato client
    if (cart.length === 0) { toast.error("Carrello vuoto"); return; }
    // Pagamenti con importo valorizzato (>0), preservando SEMPRE il metodo scelto.
    let pays = payments
      .filter((p) => Number(p.amount) > 0)
      .map((p) => ({ method: p.method || "Contanti", amount: Number(Number(p.amount).toFixed(2)) }));
    const finalR = Number(finalTotal.toFixed(2));
    let insolutoBody = null;
    let creditGenBody = null;
    if (markInsoluto) {
      // La quota non pagata diventa un insoluto sulla scheda cliente.
      // NON auto-completiamo il totale: il pagato è SOLO ciò che è stato digitato
      // (può essere 0 = insoluto totale). Questo era il bug: il totale veniva
      // riempito in automatico e l'insoluto risultava sempre 0.
      if (!clientId) { toast.error("Seleziona una cliente registrata per l'insoluto"); return; }
      const paidReal = Number(pays.reduce((s, p) => s + p.amount, 0).toFixed(2));
      const insolutoAmt = Number((finalR - paidReal).toFixed(2));
      if (insolutoAmt <= 0.01) { toast.error("Con l'insoluto l'importo pagato deve essere inferiore al totale"); return; }
      if (paidReal > finalR + 0.01) { toast.error("Il pagato non può superare il totale"); return; }
      pays = pays.filter((p) => p.amount > 0);  // può essere vuoto (insoluto totale)
      insolutoBody = { amount: insolutoAmt, note: insolutoNote };
    } else {
      // Flusso normale (nessun insoluto): se non è stato digitato alcun importo,
      // salda l'intero totale con il METODO SELEZIONATO nella prima riga.
      if (pays.length === 0) {
        pays = [{ method: payments[0]?.method || "Contanti", amount: Number(finalTotal.toFixed(2)) }];
      }
      // Verifica: totale incassato == totale scontrino (esatto, tolleranza 1 cent).
      const sumPays = Number(pays.reduce((s, p) => s + p.amount, 0).toFixed(2));
      if (genCredit && clientId && sumPays > finalR + 0.01) {
        // Sovra-pagamento: la differenza diventa credito cliente.
        const creditAmt = Number((sumPays - finalR).toFixed(2));
        creditGenBody = { amount: creditAmt, note: "Resto lasciato come credito" };
        // La vendita registra pagamenti pari al totale (con lo stesso metodo principale).
        pays = [{ method: pays[0]?.method || "Contanti", amount: finalR }];
      } else if (Math.abs(sumPays - finalR) > 0.01) {
        toast.error(`Il totale incassato (${eur(sumPays)}) deve coincidere con il totale (${eur(finalTotal)})`);
        return;
      }
    }
    try {
      setCheckingOut(true);
      // Chiave idempotenza: stabile per lo stesso tentativo di chiusura. In caso di
      // errore di rete la riusiamo, così un nuovo tentativo NON crea un duplicato.
      if (!idemKeyRef.current) {
        idemKeyRef.current = (window.crypto?.randomUUID?.() || `idem-${Date.now()}-${Math.random().toString(16).slice(2)}`);
      }
      await api.post("/sales", { idempotency_key: idemKeyRef.current, client_id: clientId || null, operator_id: operatorId || null, appointment_id: appointmentId || null, insoluto: insolutoBody, credit_generated: creditGenBody, lines: cart.map((x) => {
        const pctDisc = (appliedPct && x.item_type === "service" && appliedPct.keys.includes(svcKey(x)) && !coverage[x.ref_id])
          ? Number(((x.unit_price * x.quantity - (x.discount || 0)) * appliedPct.pct / 100).toFixed(2)) : 0;
        return { ...x, discount: Number(((x.discount || 0) + pctDisc).toFixed(2)), operator_id: operatorId || x.operator_id, covered_package_id: (x.item_type === "service" && coverage[x.ref_id]) ? coverage[x.ref_id].package_id : undefined };
      }), payments: pays, consumptions: consumptions.filter((c) => c.product_id && Number(c.qty) > 0), reward_voucher_codes: appliedVouchers.map((v) => v.code) });
      idemKeyRef.current = null; // chiusura riuscita: prossima vendita = nuova chiave
      toast.success(insolutoBody ? "Vendita registrata come insoluto" : (appointmentId ? "Appuntamento incassato" : "Vendita registrata"));
      setCart([]); setClientId(""); setPayments([{ method: "Contanti", amount: 0, _uid: uid() }]); setConsumptions([]);
      setAppointmentId(null); setCheckoutClientName(""); setAppliedVouchers([]); setVoucherCode("");
      setAppliedPct(null); setDiscPct(""); setDiscScope(new Set()); setDiscTouched(false);
      setMarkInsoluto(false); setInsolutoNote(""); setGenCredit(false);
      loadSales(); loadSession();
      api.get("/products").then((r) => setProducts(r.data));
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    finally { setCheckingOut(false); }
  };

  const useCredit = () => {
    const avail = Number(clientBalance.credit_available || 0);
    if (avail <= 0) return;
    const amt = Number(Math.min(avail, finalTotal).toFixed(2));
    setPayments((ps) => {
      const others = ps.filter((p) => p.method !== "Credito");
      return [...others, { method: "Credito", amount: amt, _uid: uid() }];
    });
    toast.success(`Credito applicato: ${eur(amt)}`);
  };

  const openPayDebt = () => {
    const d = (clientBalance.debts || []).find((x) => x.status === "open" || x.status === "partial");
    if (!d) { toast.error("Nessun insoluto aperto"); return; }
    setPayDebt(d); setPayDebtAmount(String(d.remaining)); setPayDebtMethod("Contanti");
  };

  const submitPayDebt = async () => {
    const amt = Number(payDebtAmount);
    if (!payDebt || isNaN(amt) || amt <= 0) { toast.error("Importo non valido"); return; }
    setPayDebtBusy(true);
    try {
      await api.post(`/clients/${clientId}/debts/${payDebt.id}/pay`, { amount: Number(amt.toFixed(2)), method: payDebtMethod });
      toast.success("Insoluto incassato");
      setPayDebt(null); setPayDebtAmount("");
      loadBalance(); loadSales(); loadSession();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    finally { setPayDebtBusy(false); }
  };

  const submitOpenCash = async () => {
    const val = Number(openingFloat);
    if (openingFloat === "" || isNaN(val) || val < 0) { toast.error("Inserisci il fondo cassa reale"); return; }
    await api.post("/cash/open", { opening_float: val });
    toast.success("Cassa aperta");
    setFundOpen(false); setOpeningFloat(""); loadSession();
  };
  const addMovement = async () => {
    const amt = Number(movement.amount);
    if (isNaN(amt) || amt <= 0) { toast.error("Importo non valido"); return; }
    try {
      await api.post("/cash/movement", { ...movement, amount: amt });
      toast.success("Movimento registrato");
      setMovementOpen(false); setMovement({ type: "out", method: "Contanti", category: "spese_varie", amount: "", description: "" });
      loadSession();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };
  const openCloseDialog = async () => {
    try { const { data } = await api.get("/cash/summary"); setSummary(data.summary || null); } catch { setSummary(null); }
    setOpenClose(true);
  };
  const closeCash = async () => {
    const { data } = await api.post("/cash/close", { counted: counted === "" ? null : Number(counted), notes: closeNotes });
    toast.success("Cassa chiusa");
    setOpenClose(false); setCounted(""); setCloseNotes(""); setSummary(null);
    if (data?.summary) printClosingReport(data);
    loadSession();
  };
  const printClosingReport = (data) => {
    const s = data.summary || {}; const w = window.open("", "_blank");
    if (!w) return;
    // Escape di sicurezza per i testi liberi (evita XSS via note utente).
    const esc = (v) => String(v ?? "").replace(/[&<>"']/g, (c) => (
      { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]
    ));
    const notes = data.closing_notes || closeNotes;
    const doc = w.document;
    doc.title = "Chiusura cassa";
    const style = doc.createElement("style");
    style.textContent = "body{font-family:system-ui,sans-serif;padding:24px;max-width:420px}h2{margin:0 0 4px}table{width:100%;border-collapse:collapse;margin-top:12px}td{padding:6px 0;border-bottom:1px solid #eee}td:last-child{text-align:right;font-variant-numeric:tabular-nums}.tot{font-weight:700;border-top:2px solid #333}";
    doc.head.appendChild(style);
    // I valori numerici passano da eur() (stringhe formattate sicure); le note sono
    // testo libero e vengono escapate. Nessun uso di document.write (anti-XSS).
    doc.body.innerHTML = `
      <h2>Chiusura Cassa</h2><div style="color:#666;font-size:13px">${esc(new Date().toLocaleString("it-IT", { timeZone: "Europe/Rome" }))}</div>
      <table>
      <tr><td>Fondo iniziale</td><td>${esc(eur(s.opening_float))}</td></tr>
      <tr><td>Incassi contanti</td><td>${esc(eur(s.incassi_contanti))}</td></tr>
      <tr><td>Incassi carta</td><td>${esc(eur(s.incassi_carta))}</td></tr>
      <tr><td>Altre entrate</td><td>${esc(eur(s.altre_entrate))}</td></tr>
      <tr><td>Uscite</td><td>-${esc(eur(s.uscite))}</td></tr>
      <tr class="tot"><td>Saldo teorico (contanti)</td><td>${esc(eur(s.saldo_teorico))}</td></tr>
      <tr><td>Saldo contato</td><td>${esc(eur(s.saldo_contato))}</td></tr>
      <tr class="tot"><td>Differenza</td><td>${esc(eur(s.differenza))}</td></tr>
      </table>${notes ? `<p style="margin-top:12px"><b>Note:</b> ${esc(notes)}</p>` : ""}`;
    w.focus(); w.print();
  };
  const openEditSale = (s) => setEditSale({
    id: s.id, client_id: s.client_id || "",
    lines: (s.lines || []).map((l) => ({ item_type: l.item_type, ref_id: l.ref_id, name: l.name, unit_price: l.unit_price_snapshot ?? l.unit_price ?? 0, quantity: l.quantity ?? 1, discount: l.discount ?? 0 })),
    method: (s.payments && s.payments[0]?.method) || "Contanti", reason: "",
  });
  const editSaleTotal = useMemo(() => !editSale ? 0 : editSale.lines.reduce((t, l) => t + (Number(l.unit_price) * Number(l.quantity) - Number(l.discount || 0)), 0), [editSale]);
  const saveEditSale = async () => {
    if (!editSale.reason.trim()) { toast.error("Inserisci la motivazione della correzione"); return; }
    setSavingEdit(true);
    try {
      await api.put(`/sales/${editSale.id}`, {
        reason: editSale.reason,
        client_id: editSale.client_id || null,
        client_name: clients.find((c) => c.id === editSale.client_id)?.first_name ? `${clients.find((c) => c.id === editSale.client_id).first_name} ${clients.find((c) => c.id === editSale.client_id).last_name}` : undefined,
        lines: editSale.lines.map((l) => ({ ...l, unit_price: Number(l.unit_price), quantity: Number(l.quantity), discount: Number(l.discount || 0) })),
        payments: [{ method: editSale.method, amount: editSaleTotal }],
      });
      toast.success("Scontrino corretto");
      setEditSale(null); loadSales();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore correzione"); }
    finally { setSavingEdit(false); }
  };

  const cashIn = useMemo(() => {
    if (!session) return 0;
    const movements = session.movements || [];
    const inCash = movements.filter((m) => m.type === "in" && m.method === "Contanti").reduce((s, m) => s + m.amount, 0);
    const outCash = movements.filter((m) => m.type === "out").reduce((s, m) => s + m.amount, 0);
    return (session.opening_float || 0) + inCash - outCash;
  }, [session]);

  const bookableServices = useMemo(
    () => services.filter((s) => !s.price_from || s.price > 0),
    [services]
  );

  // --- Categorie servizi (colore condiviso con Agenda) + filtro + ricerca ---
  const { categories, colorByName } = useServiceCategories();
  const [svcCatFilter, setSvcCatFilter] = useState("all");
  const [svcSearch, setSvcSearch] = useState("");
  const catNames = useMemo(() => categories.map((c) => c.name), [categories]);

  const filteredServices = useMemo(() => {
    const q = svcSearch.trim().toLowerCase();
    return bookableServices.filter((s) => {
      const cat = s.category && catNames.includes(s.category) ? s.category : UNCATEGORIZED;
      if (svcCatFilter !== "all" && cat !== svcCatFilter) return false;
      if (q && !(s.name || "").toLowerCase().includes(q)) return false;
      return true;
    });
  }, [bookableServices, svcSearch, svcCatFilter, catNames]);

  // Raggruppa per categoria (vista "Tutti"): categorie in ordine + "Da categorizzare".
  const groupedServices = useMemo(() => {
    const groups = [...catNames, UNCATEGORIZED];
    const byCat = {};
    filteredServices.forEach((s) => {
      const key = s.category && catNames.includes(s.category) ? s.category : UNCATEGORIZED;
      (byCat[key] = byCat[key] || []).push(s);
    });
    return groups.map((name) => ({ name, items: byCat[name] || [] })).filter((g) => g.items.length);
  }, [filteredServices, catNames]);

  const ServiceButton = ({ s }) => {
    const c = colorFor(colorByName, (s.category && catNames.includes(s.category)) ? s.category : null);
    return (
      <button onClick={() => addItem(s, "service")} data-testid={`pos-svc-${s.id}`}
        className="text-left rounded-lg border p-3 transition hover:brightness-[0.97]"
        style={{ borderColor: solid(c), borderLeftWidth: 4, backgroundColor: tint(c, 0.08) }}>
        <div className="text-sm font-medium truncate">{s.name}</div>
        <div className="text-xs text-muted-foreground">{s.price_from ? "da " : ""}{eur(s.price)}</div>
      </button>
    );
  };

  return (
    <div>
      <PageHeader title="Cassa" subtitle="Punto vendita e gestione cassa">
        {session ? (
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setMovementOpen(true)} data-testid="cash-movement-button"><ArrowUpCircle className="h-4 w-4 mr-1" /> Movimento</Button>
            <Button variant="outline" onClick={openCloseDialog} data-testid="cash-close-button"><Lock className="h-4 w-4 mr-1" /> Chiudi cassa</Button>
          </div>
        ) : (
          <Button onClick={() => { setOpeningFloat(""); setFundOpen(true); }} data-testid="cash-open-button"><Unlock className="h-4 w-4 mr-1" /> Apri cassa</Button>
        )}
      </PageHeader>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Catalog */}
        <div className="lg:col-span-7">
          <Tabs defaultValue="services">
            <TabsList><TabsTrigger value="services" data-testid="cassa-tab-services">Servizi</TabsTrigger><TabsTrigger value="products" data-testid="cassa-tab-products">Prodotti</TabsTrigger><TabsTrigger value="packages" data-testid="cassa-tab-packages">Pacchetti</TabsTrigger><TabsTrigger value="subs" data-testid="cassa-tab-subs">Abbonamenti</TabsTrigger></TabsList>
            <TabsContent value="services" className="mt-3">
              {/* Barra categorie + ricerca */}
              <div className="flex items-center gap-2 mb-3 flex-wrap">
                <div className="relative flex-1 min-w-[160px]">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input value={svcSearch} onChange={(e) => setSvcSearch(e.target.value)} placeholder="Cerca servizio..." className="h-9 pl-8" data-testid="cassa-service-search" />
                </div>
              </div>
              <div className="flex items-center gap-1.5 mb-3 flex-wrap" data-testid="cassa-category-bar">
                <button onClick={() => setSvcCatFilter("all")} data-testid="cassa-cat-all"
                  className={`rounded-full px-3 py-1 text-xs font-medium border transition ${svcCatFilter === "all" ? "bg-foreground text-background border-foreground" : "border-border hover:bg-accent"}`}>
                  Tutti
                </button>
                {categories.map((c) => (
                  <button key={c.id} onClick={() => setSvcCatFilter(c.name)} data-testid={`cassa-cat-${c.id}`}
                    className="rounded-full px-3 py-1 text-xs font-medium border transition inline-flex items-center gap-1.5"
                    style={svcCatFilter === c.name
                      ? { backgroundColor: solid(c.color), borderColor: solid(c.color), color: "#fff" }
                      : { borderColor: solid(c.color), backgroundColor: tint(c.color, 0.08) }}>
                    <span className="h-2 w-2 rounded-full" style={{ backgroundColor: svcCatFilter === c.name ? "#fff" : solid(c.color) }} />
                    {c.name}
                  </button>
                ))}
              </div>

              {svcCatFilter === "all" ? (
                <div className="space-y-4">
                  {groupedServices.map((g) => {
                    const gc = colorFor(colorByName, g.name === UNCATEGORIZED ? null : g.name);
                    return (
                      <div key={g.name} data-testid={`cassa-group-${g.name}`}>
                        <div className="flex items-center gap-2 mb-2">
                          <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: solid(gc) }} />
                          <h4 className="text-sm font-semibold">{g.name}</h4>
                          <span className="text-xs text-muted-foreground">({g.items.length})</span>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                          {g.items.map((s) => <ServiceButton key={s.id} s={s} />)}
                        </div>
                      </div>
                    );
                  })}
                  {!groupedServices.length && <div className="text-sm text-muted-foreground py-6 text-center">Nessun servizio trovato.</div>}
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {filteredServices.map((s) => <ServiceButton key={s.id} s={s} />)}
                  {!filteredServices.length && <div className="col-span-full text-sm text-muted-foreground py-6 text-center">Nessun servizio trovato.</div>}
                </div>
              )}
            </TabsContent>
            <TabsContent value="products" className="mt-3">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {products.map((p) => (
                  <button key={p.id} onClick={() => addItem(p, "product")} data-testid={`pos-prod-${p.id}`} className="text-left rounded-lg border border-border p-3 hover:bg-accent">
                    <div className="text-sm font-medium truncate">{p.name}</div>
                    <div className="text-xs text-muted-foreground">{eur(p.price)} · stock {p.stock}</div>
                  </button>
                ))}
              </div>
            </TabsContent>
            <TabsContent value="packages" className="mt-3">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {catalog.packages.length === 0 && <div className="col-span-full text-sm text-muted-foreground py-4 text-center">Nessun pacchetto. Creane uno nella sezione Pacchetti.</div>}
                {catalog.packages.map((p) => (
                  <button key={p.id} onClick={() => addItem(p, "package")} data-testid={`pos-pkg-${p.id}`} className="text-left rounded-lg border border-border p-3 hover:bg-accent">
                    <div className="text-sm font-medium truncate">{p.name}</div>
                    <div className="text-xs text-muted-foreground">{eur(p.price)}</div>
                  </button>
                ))}
              </div>
            </TabsContent>
            <TabsContent value="subs" className="mt-3">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {catalog.subscriptions.length === 0 && <div className="col-span-full text-sm text-muted-foreground py-4 text-center">Nessun abbonamento. Creane uno nella sezione Pacchetti.</div>}
                {catalog.subscriptions.map((s) => (
                  <button key={s.id} onClick={() => addItem(s, "subscription")} data-testid={`pos-sub-${s.id}`} className="text-left rounded-lg border border-border p-3 hover:bg-accent">
                    <div className="text-sm font-medium truncate">{s.name}</div>
                    <div className="text-xs text-muted-foreground">{eur(s.price)}</div>
                  </button>
                ))}
              </div>
            </TabsContent>
          </Tabs>

          <Card className="mt-6 p-0">
            <div className="p-4 border-b border-border flex items-center justify-between"><h3 className="font-heading text-lg font-semibold">Vendite di oggi</h3>{session && <Badge variant="secondary">Contanti in cassa: {eur(cashIn)}</Badge>}</div>
            <div className="max-h-64 overflow-y-auto hi-scroll">
              {sales.length === 0 && <div className="p-6 text-center text-sm text-muted-foreground">Nessuna vendita oggi</div>}
              {sales.map((s) => {
                const cancelled = s.status === "cancelled";
                return (
                <div key={s.id} className={`flex items-center justify-between px-4 py-2.5 border-b border-border/50 text-sm ${cancelled ? "opacity-60" : ""}`} data-testid={`sale-row-${s.id}`}>
                  <div className="flex items-center gap-2"><Receipt className="h-4 w-4 text-muted-foreground" /><span className={cancelled ? "line-through" : ""}>{s.client_name}</span><span className="text-xs text-muted-foreground">{fmtTime(s.created_at)}</span></div>
                  <div className="flex items-center gap-2">
                    <span className={`font-medium tabular ${cancelled ? "line-through" : ""}`}>{eur(s.total)}</span>
                    {cancelled
                      ? <Badge variant="secondary" className="text-[10px] text-[hsl(var(--hi-status-danger))]" data-testid={`sale-cancelled-${s.id}`}>Annullata</Badge>
                      : s.edited && <Badge variant="secondary" className="ml-1.5 text-[10px]">corretto</Badge>}
                    {isAdmin && !cancelled && (
                      <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => openEditSale(s)} data-testid={`sale-edit-${s.id}`}>
                        <Pencil className="h-3.5 w-3.5 mr-1" /> Correggi
                      </Button>
                    )}
                    {canCancelSale && !cancelled && (
                      <Button variant="ghost" size="sm" className="h-7 px-2 text-xs text-[hsl(var(--hi-status-danger))]" onClick={() => setCancelTarget(s)} data-testid={`sale-cancel-${s.id}`}>
                        <Ban className="h-3.5 w-3.5 mr-1" /> Annulla
                      </Button>
                    )}
                    <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/sales/${s.id}/receipt`, "_blank")} data-testid={`sale-receipt-${s.id}`}>
                      <FileText className="h-3.5 w-3.5 mr-1" /> Ricevuta
                    </Button>
                  </div>
                </div>
              );})}
            </div>
          </Card>
        </div>

        {/* Conferma annullamento vendita (Admin/Manager) */}
        <Dialog open={!!cancelTarget} onOpenChange={(o) => { if (!o) setCancelTarget(null); }}>
          <DialogContent className="max-w-md" data-testid="cancel-sale-dialog">
            <DialogHeader><DialogTitle className="font-heading text-xl">Annulla vendita</DialogTitle></DialogHeader>
            <div className="space-y-2 text-sm">
              <p>Confermi l'annullamento della vendita di <strong>{cancelTarget?.client_name}</strong> ({eur(cancelTarget?.total)})?</p>
              <p className="text-muted-foreground text-[13px]">La vendita resterà nello storico come <strong>Annullata</strong> ed esclusa da incasso, statistiche e commissioni. Verranno ripristinati eventuali pacchetti, gift card, credito, punti e giacenze collegati.</p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCancelTarget(null)} data-testid="cancel-sale-abort">Torna indietro</Button>
              <Button variant="destructive" onClick={confirmCancelSale} disabled={cancelBusy} data-testid="cancel-sale-confirm">{cancelBusy ? "Annullamento…" : "Annulla vendita"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        {/* Cart */}
        <div className="lg:col-span-5">
          <Card className="p-4 sticky top-20">
            <h3 className="font-heading text-xl font-semibold mb-3">Carrello</h3>
            {appointmentId && (
              <div className="mb-3 rounded-lg bg-[hsl(var(--hi-gold-soft))] border border-[hsl(var(--hi-gold)/0.35)] px-3 py-2 text-sm flex items-center gap-2" data-testid="checkout-appt-banner">
                <CalendarCheck className="h-4 w-4 text-[hsl(var(--hi-gold))]" />
                <span>Checkout appuntamento · <span className="font-medium">{checkoutClientName}</span></span>
              </div>
            )}
            <div className="grid grid-cols-2 gap-2 mb-3">
              <Select value={clientId} onValueChange={setClientId}><SelectTrigger data-testid="pos-client"><SelectValue placeholder="Cliente" /></SelectTrigger><SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.first_name} {c.last_name}</SelectItem>)}</SelectContent></Select>
              <Select value={operatorId} onValueChange={setOperatorId}><SelectTrigger data-testid="pos-operator"><SelectValue placeholder="Operatore" /></SelectTrigger><SelectContent>{team.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent></Select>
            </div>
            {referralWelcome && (
              <div className="mb-3 rounded-lg bg-[hsl(var(--hi-gold-soft))] border border-[hsl(var(--hi-gold)/0.35)] px-3 py-2 text-sm flex items-center justify-between gap-2" data-testid="referral-welcome-badge">
                <span className="flex items-center gap-2 min-w-0"><Gift className="h-4 w-4 text-[hsl(var(--hi-gold))] shrink-0" /><span className="truncate">Promo disponibile · <span className="font-medium">Porta un'amica -{referralWelcome}%</span></span></span>
                <Button variant="outline" size="sm" className="h-7 px-2 text-xs shrink-0" onClick={useReferralPromo} data-testid="referral-use-button">Usa</Button>
              </div>
            )}
            <div className="space-y-2 max-h-64 overflow-y-auto hi-scroll mb-3">
              {cart.length === 0 && <div className="text-sm text-muted-foreground text-center py-6">Aggiungi servizi o prodotti</div>}
              {cart.map((it, i) => {
                const covered = it.item_type === "service" && coverage[it.ref_id];
                const coverable = it.item_type === "service" && (entitlements.packages || []).some((p) => (p.items || []).some((x) => x.service_id === it.ref_id && x.available > 0));
                return (
                <div key={`${it.item_type}-${it.ref_id}`} className="rounded-lg border border-border p-2">
                  <div className="flex justify-between items-center"><span className="text-sm font-medium truncate">{it.name}</span><Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => rm(i)}><X className="h-3.5 w-3.5" /></Button></div>
                  <div className="flex items-center justify-between mt-1">
                    <div className="flex items-center gap-1"><Button variant="outline" size="icon" className="h-6 w-6" onClick={() => updQty(i, -1)}><Minus className="h-3 w-3" /></Button><span className="w-6 text-center text-sm tabular">{it.quantity}</span><Button variant="outline" size="icon" className="h-6 w-6" onClick={() => updQty(i, 1)}><Plus className="h-3 w-3" /></Button></div>
                    <div className="flex items-center gap-1"><span className="text-[11px] text-muted-foreground">Sconto</span><Input type="number" value={it.discount} onChange={(e) => updDisc(i, e.target.value)} className="h-6 w-16 text-xs" disabled={!!covered} /></div>
                    <span className="text-sm font-medium tabular">{covered ? <span className="text-[hsl(var(--hi-status-success))]">€0</span> : eur(it.unit_price * it.quantity - it.discount)}</span>
                  </div>
                  {coverable && (
                    <div className="mt-1.5 flex items-center justify-between gap-2 rounded-md bg-[hsl(var(--hi-gold-soft))] px-2 py-1" data-testid={`cart-coverage-${it.ref_id}`}>
                      <span className="text-[11px] text-[hsl(var(--hi-gold))] font-medium truncate">
                        {covered ? `Coperto da: ${covered.package_name}` : "Pacchetto disponibile"}
                      </span>
                      <Button variant="ghost" size="sm" className="h-6 px-2 text-[11px]" onClick={() => toggleUncover(it.ref_id)} data-testid={`cart-coverage-toggle-${it.ref_id}`}>
                        {covered ? "Annulla" : "Usa pacchetto"}
                      </Button>
                    </div>
                  )}
                </div>
                );
              })}
            </div>
            {/* Fase 5B: consumo materiali (magazzino) */}
            {cart.some((x) => x.item_type === "service") && (
              <div className="space-y-2 border-t border-border pt-3 mb-1" data-testid="consumption-section">
                <div className="flex items-center justify-between">
                  <Label className="text-xs flex items-center gap-1.5"><FlaskConical className="h-3.5 w-3.5 text-[hsl(var(--hi-gold))]" /> Consumo materiali</Label>
                  <Button variant="ghost" size="sm" className="h-6 text-xs" onClick={addCons} data-testid="consumption-add"><Plus className="h-3 w-3 mr-1" /> Materiale</Button>
                </div>
                {consumptions.length === 0 && <p className="text-[11px] text-muted-foreground">Prodotti scaricati dal magazzino a fine servizio. Precompilato dalla ricetta standard.</p>}
                {consumptions.map((cn, i) => {
                  const prod = products.find((p) => p.id === cn.product_id);
                  return (
                    <div key={cn._uid} className="flex gap-2 items-center" data-testid={`consumption-row-${i}`}>
                      <Select value={cn.product_id} onValueChange={(v) => setCons(i, "product_id", v)}>
                        <SelectTrigger className="flex-1 h-8 text-xs" data-testid={`consumption-product-${i}`}><SelectValue placeholder="Prodotto" /></SelectTrigger>
                        <SelectContent>{consumableProducts.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} ({p.unit})</SelectItem>)}</SelectContent>
                      </Select>
                      <Input type="number" value={cn.qty} onChange={(e) => setCons(i, "qty", e.target.value)} className="h-8 w-20 text-xs" placeholder={prod?.unit || "qtà"} data-testid={`consumption-qty-${i}`} />
                      <span className="text-[11px] text-muted-foreground w-6">{prod?.unit || ""}</span>
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => rmCons(i)}><X className="h-3.5 w-3.5" /></Button>
                    </div>
                  );
                })}
              </div>
            )}
            <div className="space-y-2 border-t border-border pt-3 mb-1" data-testid="voucher-section">
              <Label className="text-xs flex items-center gap-1.5"><Ticket className="h-3.5 w-3.5 text-[hsl(var(--hi-gold))]" /> Premi / Voucher</Label>
              <div className="flex gap-2">
                <Input value={voucherCode} onChange={(e) => setVoucherCode(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); applyVoucher(); } }} placeholder="Codice voucher (RW-...)" className="h-8 text-sm" data-testid="voucher-input" />
                <Button variant="outline" size="sm" className="h-8" onClick={applyVoucher} disabled={voucherBusy || !voucherCode.trim()} data-testid="voucher-apply">Applica</Button>
              </div>
              {appliedVouchers.map((v) => (
                <div key={v.code} className="flex items-center justify-between rounded-md bg-[hsl(var(--hi-gold-soft))] px-2.5 py-1.5 text-xs" data-testid={`voucher-applied-${v.code}`}>
                  <span className="flex items-center gap-1.5 min-w-0"><Ticket className="h-3.5 w-3.5 text-[hsl(var(--hi-gold))] shrink-0" /><span className="font-mono font-medium">{v.code}</span><span className="text-muted-foreground truncate">· {v.reward_name}</span></span>
                  <span className="flex items-center gap-1.5 shrink-0"><span className="font-medium text-[hsl(var(--hi-gold))]">-{eur(v.applied)}</span><Button variant="ghost" size="icon" className="h-5 w-5" onClick={() => removeVoucher(v.code)} data-testid={`voucher-remove-${v.code}`}><X className="h-3 w-3" /></Button></span>
                </div>
              ))}
            </div>
            {/* --- Applica sconto (percentuale, simulazione fino a conferma) --- */}
            {serviceLinesDisc.length > 0 && (
              <div className="space-y-2.5 border-t border-border pt-3" data-testid="discount-section">
                <div className="flex items-center justify-between">
                  <Label className="text-xs flex items-center gap-1.5"><Percent className="h-3.5 w-3.5 text-[hsl(var(--hi-gold))]" /> Applica sconto</Label>
                  {appliedPct && <Badge className="rounded-full bg-[hsl(var(--hi-status-success))]/12 text-[hsl(var(--hi-status-success))] text-[11px]" data-testid="discount-applied-badge">Applicato -{appliedPct.pct}%</Badge>}
                </div>

                {/* Selezione servizi interessati */}
                <div className="rounded-lg border border-border p-2 space-y-1">
                  <div className="flex items-center justify-between">
                    <span className="text-[11px] uppercase tracking-wider text-muted-foreground">Servizi interessati</span>
                    <button type="button" onClick={toggleAllDisc} disabled={!!appliedPct} className="text-[11px] font-medium text-[hsl(var(--hi-gold))] hover:text-[hsl(var(--hi-gold-hover))] disabled:opacity-40" data-testid="discount-select-all">{allDiscSelected ? "Deseleziona tutti" : "Seleziona tutti"}</button>
                  </div>
                  {serviceLinesDisc.map((l) => (
                    <label key={svcKey(l)} htmlFor={`disc-${svcKey(l)}`} className="flex items-center justify-between gap-2 py-0.5 cursor-pointer">
                      <span className="flex items-center gap-2 min-w-0">
                        <Checkbox id={`disc-${svcKey(l)}`} checked={effectiveDiscKeys.includes(svcKey(l))} onCheckedChange={() => toggleDiscKey(svcKey(l))} disabled={!!appliedPct} data-testid={`discount-svc-${l.ref_id}`} />
                        <span className="text-sm truncate">{l.name}</span>
                      </span>
                      <span className="text-sm tabular text-muted-foreground shrink-0">{eur(lineNet(l))}</span>
                    </label>
                  ))}
                </div>

                {/* Percentuale + azione */}
                <div className="flex items-center gap-2">
                  <div className="relative flex-1">
                    <Input type="number" min="0" max="100" value={discPct} onChange={(e) => setDiscPct(e.target.value)} placeholder="Percentuale sconto" className="h-9 pr-7" disabled={!!appliedPct} data-testid="discount-pct-input" />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">%</span>
                  </div>
                  {!appliedPct ? (
                    <Button size="sm" className="h-9" onClick={applyDiscount} disabled={pctNum <= 0 || effectiveDiscKeys.length === 0} data-testid="discount-apply">Applica sconto</Button>
                  ) : (
                    <Button size="sm" variant="outline" className="h-9" onClick={removeDiscount} data-testid="discount-remove"><X className="h-3.5 w-3.5 mr-1" /> Rimuovi</Button>
                  )}
                </div>

                {/* Riepilogo live (simulazione o applicato) */}
                {(pctNum > 0 || appliedPct) && (() => {
                  const info = appliedPct ? { pct: appliedPct.pct, keys: appliedPct.keys, amount: appliedAmount } : { pct: pctNum, keys: effectiveDiscKeys, amount: simDiscountAmount };
                  const names = serviceLinesDisc.filter((l) => info.keys.includes(svcKey(l))).map((l) => l.name);
                  return (
                    <div className="rounded-lg bg-[hsl(var(--hi-gold-soft))] border border-[hsl(var(--hi-gold)/0.3)] p-2.5 space-y-1 text-xs" data-testid="discount-preview">
                      <div className="flex justify-between"><span className="text-muted-foreground">Totale senza sconto</span><span className="tabular">{eur(netTotal)}</span></div>
                      <div className="flex justify-between"><span className="text-muted-foreground">Sconto</span><span className="tabular">{info.pct}%</span></div>
                      <div className="flex justify-between gap-3"><span className="text-muted-foreground shrink-0">Servizi interessati</span><span className="text-right truncate">{names.length === serviceLinesDisc.length ? "Tutti i servizi" : names.join(", ")}</span></div>
                      <div className="flex justify-between"><span className="text-muted-foreground">Importo sconto</span><span className="tabular text-[hsl(var(--hi-gold))] font-medium">-{eur(info.amount)}</span></div>
                      <div className="flex justify-between border-t border-[hsl(var(--hi-gold)/0.3)] pt-1 mt-1"><span className="font-medium">Totale con sconto</span><span className="tabular font-semibold">{eur(Math.max(0, netTotal - info.amount))}</span></div>
                      {!appliedPct && <p className="text-[11px] text-muted-foreground pt-0.5" data-testid="discount-sim-note">Simulazione — premi "Applica sconto" per confermare. Senza conferma il totale resta {eur(netTotal)}.</p>}
                    </div>
                  );
                })()}
              </div>
            )}

            {clientId && (clientBalance.open_debts_total > 0 || clientBalance.credit_available > 0) && (
              <div className="space-y-2 border-t border-border pt-3" data-testid="cassa-balance-banners">
                {clientBalance.open_debts_total > 0 && (
                  <div className="flex items-center justify-between gap-2 rounded-lg border border-[hsl(38_92%_45%/0.45)] bg-[hsl(38_92%_50%/0.1)] px-3 py-2" data-testid="cassa-insoluto-banner">
                    <span className="text-sm flex items-center gap-1.5"><AlertTriangle className="h-4 w-4 text-[hsl(38_92%_42%)]" /> Insoluto precedente <b>{eur(clientBalance.open_debts_total)}</b></span>
                    <Button size="sm" variant="outline" className="h-8" onClick={openPayDebt} data-testid="cassa-incassa-insoluto">Incassa insoluto</Button>
                  </div>
                )}
                {clientBalance.credit_available > 0 && (
                  <div className="flex items-center justify-between gap-2 rounded-lg border border-[hsl(var(--hi-status-success)/0.45)] bg-[hsl(var(--hi-status-success)/0.08)] px-3 py-2" data-testid="cassa-credito-banner">
                    <span className="text-sm flex items-center gap-1.5"><Wallet className="h-4 w-4 text-[hsl(var(--hi-status-success))]" /> Credito disponibile <b>{eur(clientBalance.credit_available)}</b></span>
                    <Button size="sm" variant="outline" className="h-8" onClick={useCredit} data-testid="cassa-usa-credito">Usa credito</Button>
                  </div>
                )}
              </div>
            )}

            <div className="space-y-2 border-t border-border pt-3">
              <Label className="text-xs">Pagamento</Label>
              {payments.map((p, i) => (
                <div key={p._uid} className="flex gap-2">
                  <Select value={p.method} onValueChange={(v) => setPayments((ps) => ps.map((x, idx) => idx === i ? { ...x, method: v } : x))}><SelectTrigger className="flex-1" data-testid={`pos-payment-method-${i}`}><SelectValue /></SelectTrigger><SelectContent>{METHODS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent></Select>
                  <Input type="number" value={p.amount} onChange={(e) => setPayments((ps) => ps.map((x, idx) => idx === i ? { ...x, amount: e.target.value } : x))} className="w-24" placeholder={i === 0 ? "auto" : "0"} data-testid={`pos-payment-${i}`} />
                  {payments.length > 1 && <Button variant="ghost" size="icon" onClick={() => setPayments((ps) => ps.filter((_, idx) => idx !== i))}><X className="h-4 w-4" /></Button>}
                </div>
              ))}
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" onClick={() => setPayments((ps) => [...ps, { method: "Carta", amount: 0, _uid: uid() }])} data-testid="pos-add-mixed"><Plus className="h-3.5 w-3.5 mr-1" /> Pagamento misto</Button>
                {remaining > 0.01 && (
                  <Button variant="outline" size="sm" data-testid="pos-fill-remaining"
                    onClick={() => setPayments((ps) => {
                      const idx = [...ps].reverse().findIndex((x) => !(Number(x.amount) > 0));
                      const realIdx = idx === -1 ? ps.length - 1 : ps.length - 1 - idx;
                      return ps.map((x, j) => j === realIdx ? { ...x, amount: Number((Number(x.amount || 0) + remaining).toFixed(2)) } : x);
                    })}>
                    Salda rimanente ({eur(remaining)})
                  </Button>
                )}
              </div>
              {payments.filter((p) => Number(p.amount) > 0).length > 1 && (
                <div className={`text-xs ${Math.abs(remaining) <= 0.01 ? "text-[hsl(var(--hi-status-success))]" : "text-[hsl(var(--hi-status-warning,38_92%_50%))]"}`} data-testid="pos-mixed-status">
                  {Math.abs(remaining) <= 0.01 ? "Pagamento misto quadrato col totale" : `Rimanente da assegnare: ${eur(remaining)}`}
                </div>
              )}
            </div>
            {clientId && (
              <div className="space-y-2 pt-1">
                <label className="flex items-center gap-2 cursor-pointer" data-testid="cassa-mark-insoluto-row">
                  <Checkbox checked={markInsoluto} onCheckedChange={(v) => { setMarkInsoluto(!!v); if (v) setGenCredit(false); }} data-testid="cassa-mark-insoluto" />
                  <span className="text-sm flex items-center gap-1.5"><AlertTriangle className="h-3.5 w-3.5 text-[hsl(38_92%_42%)]" /> Segna come insoluto (non pagato ora)</span>
                </label>
                {markInsoluto && (
                  <Input value={insolutoNote} onChange={(e) => setInsolutoNote(e.target.value)} placeholder="Nota insoluto (facoltativa)" className="h-8 text-xs" data-testid="cassa-insoluto-note" />
                )}
                {!markInsoluto && paid > finalTotal + 0.01 && (
                  <label className="flex items-center gap-2 cursor-pointer" data-testid="cassa-gen-credit-row">
                    <Checkbox checked={genCredit} onCheckedChange={(v) => setGenCredit(!!v)} data-testid="cassa-gen-credit" />
                    <span className="text-sm flex items-center gap-1.5"><Wallet className="h-3.5 w-3.5 text-[hsl(var(--hi-status-success))]" /> Lascia il resto ({eur(paid - finalTotal)}) come credito</span>
                  </label>
                )}
              </div>
            )}
            {voucherDiscount > 0 && (
              <div className="flex justify-between items-center text-sm pt-2" data-testid="voucher-discount-row">
                <span className="text-[hsl(var(--hi-gold))]">Sconto premi</span>
                <span className="font-medium text-[hsl(var(--hi-gold))] tabular">-{eur(voucherDiscount)}</span>
              </div>
            )}
            {appliedPct && (
              <div className="flex justify-between items-center text-sm pt-2" data-testid="discount-total-row">
                <span className="text-[hsl(var(--hi-gold))]">Sconto {appliedPct.pct}%</span>
                <span className="font-medium text-[hsl(var(--hi-gold))] tabular">-{eur(appliedAmount)}</span>
              </div>
            )}
            <div className="border-t border-border pt-3 mt-2">
              {appliedPct && (
                <div className="flex justify-between items-center text-sm mb-0.5" data-testid="cassa-total-original">
                  <span className="text-muted-foreground">Totale senza sconto</span>
                  <span className="tabular line-through text-muted-foreground">{eur(netTotal)}</span>
                </div>
              )}
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">{appliedPct ? "Totale con sconto" : "Totale"}</span>
                <span className="font-heading text-2xl font-semibold tabular" data-testid="cassa-total">{eur(finalTotal)}</span>
              </div>
            </div>
            <Button className="w-full mt-3" onClick={checkout} disabled={checkingOut} data-testid="pos-checkout-button">{checkingOut ? "Registrazione…" : (markInsoluto ? `Registra insoluto ${eur(Math.max(0, finalTotal - paid))}` : `Incassa ${eur(finalTotal)}`)}</Button>
            <div className="text-xs text-muted-foreground text-center mt-1">Pagato: {eur(paid)} · Resto: {eur(Math.max(0, paid - finalTotal))}</div>
          </Card>
        </div>
      </div>

      <Dialog open={!!payDebt} onOpenChange={(o) => !o && setPayDebt(null)}>
        <DialogContent data-testid="cassa-paydebt-dialog">
          <DialogHeader><DialogTitle className="font-heading text-xl">Incassa insoluto</DialogTitle></DialogHeader>
          {payDebt && (
            <div className="space-y-3">
              <div className="text-sm text-muted-foreground">
                Residuo insoluto: <b className="text-foreground">{eur(payDebt.remaining)}</b>
                {payDebt.service_names?.length ? <span> · {payDebt.service_names.join(", ")}</span> : null}
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Importo da incassare</Label>
                <Input type="number" value={payDebtAmount} onChange={(e) => setPayDebtAmount(e.target.value)} data-testid="cassa-paydebt-amount" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Metodo di pagamento</Label>
                <Select value={payDebtMethod} onValueChange={setPayDebtMethod}>
                  <SelectTrigger data-testid="cassa-paydebt-method"><SelectValue /></SelectTrigger>
                  <SelectContent>{METHODS.filter((m) => m !== "Credito" && m !== "Gift Card").map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <p className="text-[11px] text-muted-foreground">L'incasso entra nella cassa di oggi senza generare nuovo fatturato servizi.</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayDebt(null)}>Annulla</Button>
            <Button onClick={submitPayDebt} disabled={payDebtBusy} data-testid="cassa-paydebt-confirm">Incassa</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={openClose} onOpenChange={setOpenClose}>
        <DialogContent data-testid="cash-close-dialog">
          <DialogHeader><DialogTitle className="font-heading text-2xl">Chiusura cassa</DialogTitle></DialogHeader>
          <div className="space-y-2">
            {summary && (
              <div className="rounded-lg border border-border p-3 space-y-1.5 text-sm">
                <Row label="Fondo iniziale" value={eur(summary.opening_float)} />
                <Row label="Incassi contanti" value={eur(summary.incassi_contanti)} />
                <Row label="Incassi carta" value={eur(summary.incassi_carta)} />
                <Row label="Altre entrate" value={eur(summary.altre_entrate)} />
                <Row label="Uscite" value={`-${eur(summary.uscite)}`} />
                <div className="border-t border-border pt-1.5"><Row label="Saldo teorico (contanti)" value={eur(summary.saldo_teorico)} bold /></div>
              </div>
            )}
            <div className="space-y-1.5"><Label>Contanti contati in cassa</Label><Input type="number" value={counted} onChange={(e) => setCounted(e.target.value)} data-testid="cash-counted" placeholder="0.00" /></div>
            {counted !== "" && summary && (
              <div className={`flex justify-between text-sm font-medium ${Number(counted) - summary.saldo_teorico === 0 ? "text-[hsl(var(--hi-status-success))]" : "text-destructive"}`} data-testid="cash-difference">
                <span>Differenza</span><span>{eur(Number(counted) - summary.saldo_teorico)}</span>
              </div>
            )}
            <div className="space-y-1.5"><Label>Note finali</Label><Textarea rows={2} value={closeNotes} onChange={(e) => setCloseNotes(e.target.value)} data-testid="cash-notes" /></div>
            <Button className="w-full" onClick={closeCash} data-testid="cash-close-confirm"><Printer className="h-4 w-4 mr-1.5" /> Conferma e stampa report</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Apertura cassa: fondo reale */}
      <Dialog open={fundOpen} onOpenChange={setFundOpen}>
        <DialogContent className="max-w-sm" data-testid="cash-fund-dialog">
          <DialogHeader><DialogTitle className="font-heading text-2xl">Apri cassa</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>Fondo cassa iniziale (contante reale)</Label><Input type="number" autoFocus value={openingFloat} onChange={(e) => setOpeningFloat(e.target.value)} placeholder="Es. 120.00" data-testid="cash-fund-input" onKeyDown={(e) => { if (e.key === "Enter") submitOpenCash(); }} /></div>
            <p className="text-xs text-muted-foreground">Conta il contante realmente presente in cassa per evitare differenze e grafici errati.</p>
            <DialogFooter>
              <Button variant="outline" onClick={() => setFundOpen(false)}>Annulla</Button>
              <Button onClick={submitOpenCash} data-testid="cash-fund-confirm">Apri cassa</Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* Movimento di cassa */}
      <Dialog open={movementOpen} onOpenChange={setMovementOpen}>
        <DialogContent className="max-w-sm" data-testid="cash-movement-dialog">
          <DialogHeader><DialogTitle className="font-heading text-2xl">Movimento di cassa</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <Button variant={movement.type === "in" ? "default" : "outline"} onClick={() => setMovement({ ...movement, type: "in" })} data-testid="movement-type-in"><ArrowDownCircle className="h-4 w-4 mr-1" /> Entrata</Button>
              <Button variant={movement.type === "out" ? "default" : "outline"} onClick={() => setMovement({ ...movement, type: "out" })} data-testid="movement-type-out"><ArrowUpCircle className="h-4 w-4 mr-1" /> Uscita</Button>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1.5"><Label>Categoria</Label>
                <Select value={movement.category} onValueChange={(v) => setMovement({ ...movement, category: v })}>
                  <SelectTrigger data-testid="movement-category"><SelectValue /></SelectTrigger>
                  <SelectContent>{MOVEMENT_CATEGORIES.map((c) => <SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label>Metodo</Label>
                <Select value={movement.method} onValueChange={(v) => setMovement({ ...movement, method: v })}>
                  <SelectTrigger data-testid="movement-method"><SelectValue /></SelectTrigger>
                  <SelectContent>{METHODS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5"><Label>Importo</Label><Input type="number" value={movement.amount} onChange={(e) => setMovement({ ...movement, amount: e.target.value })} placeholder="0.00" data-testid="movement-amount" /></div>
            <div className="space-y-1.5"><Label>Descrizione</Label><Input value={movement.description} onChange={(e) => setMovement({ ...movement, description: e.target.value })} data-testid="movement-description" /></div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setMovementOpen(false)}>Annulla</Button>
              <Button onClick={addMovement} data-testid="movement-confirm">Registra</Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* Correzione scontrino (Admin) */}
      <Dialog open={!!editSale} onOpenChange={(o) => !o && setEditSale(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto hi-scroll" data-testid="sale-edit-dialog">
          <DialogHeader><DialogTitle className="font-heading text-2xl">Correggi scontrino</DialogTitle></DialogHeader>
          {editSale && (
            <div className="space-y-3">
              <div className="space-y-1.5"><Label>Cliente</Label>
                <Select value={editSale.client_id} onValueChange={(v) => setEditSale({ ...editSale, client_id: v })}>
                  <SelectTrigger data-testid="edit-sale-client"><SelectValue placeholder="Nessuno" /></SelectTrigger>
                  <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.first_name} {c.last_name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Righe</Label>
                {editSale.lines.map((l, i) => (
                  <div key={i} className="rounded-lg border border-border p-2 space-y-1.5" data-testid={`edit-sale-line-${i}`}>
                    <div className="text-sm font-medium truncate">{l.name}</div>
                    <div className="grid grid-cols-3 gap-2">
                      <div><Label className="text-[10px] text-muted-foreground">Prezzo</Label><Input type="number" className="h-8" value={l.unit_price} onChange={(e) => setEditSale({ ...editSale, lines: editSale.lines.map((x, idx) => idx === i ? { ...x, unit_price: e.target.value } : x) })} data-testid={`edit-line-price-${i}`} /></div>
                      <div><Label className="text-[10px] text-muted-foreground">Qtà</Label><Input type="number" className="h-8" value={l.quantity} onChange={(e) => setEditSale({ ...editSale, lines: editSale.lines.map((x, idx) => idx === i ? { ...x, quantity: e.target.value } : x) })} data-testid={`edit-line-qty-${i}`} /></div>
                      <div><Label className="text-[10px] text-muted-foreground">Sconto</Label><Input type="number" className="h-8" value={l.discount} onChange={(e) => setEditSale({ ...editSale, lines: editSale.lines.map((x, idx) => idx === i ? { ...x, discount: e.target.value } : x) })} data-testid={`edit-line-disc-${i}`} /></div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-2 gap-2 items-end">
                <div className="space-y-1.5"><Label>Metodo pagamento</Label>
                  <Select value={editSale.method} onValueChange={(v) => setEditSale({ ...editSale, method: v })}>
                    <SelectTrigger data-testid="edit-sale-method"><SelectValue /></SelectTrigger>
                    <SelectContent>{METHODS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="text-right"><div className="text-xs text-muted-foreground">Nuovo totale</div><div className="font-heading text-2xl font-semibold" data-testid="edit-sale-total">{eur(editSaleTotal)}</div></div>
              </div>
              <div className="space-y-1.5"><Label>Motivazione della correzione *</Label><Textarea rows={2} value={editSale.reason} onChange={(e) => setEditSale({ ...editSale, reason: e.target.value })} placeholder="Es. errore importo, servizio errato..." data-testid="edit-sale-reason" /></div>
              <p className="text-[11px] text-muted-foreground">La modifica viene registrata nel log (data, ora, operatore, valori precedenti/nuovi). Punti fedeltà e magazzino non vengono modificati automaticamente.</p>
              <DialogFooter>
                <Button variant="outline" onClick={() => setEditSale(null)}>Annulla</Button>
                <Button onClick={saveEditSale} disabled={savingEdit} data-testid="edit-sale-save">{savingEdit ? "Salvataggio..." : "Salva correzione"}</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Row({ label, value, bold }) {
  return <div className="flex justify-between"><span className="text-muted-foreground">{label}</span><span className={`tabular ${bold ? "font-semibold" : ""}`}>{value}</span></div>;
}
